import React, { useState, useEffect, useRef } from 'react';
import styles from './Navbar.module.css';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAlpaca } from '../../context/AlpacaContext';
import { useAccountMode } from '../../context/AccountModeContext';
import { useTheme } from '../../context/ThemeContext';
import logoIcon from '../../assets/logo-icon.png';
import NotificationBell from '../NotificationBell/NotificationBell';

interface NavGroup {
  label: string;
  items: { to: string; label: string }[];
  authOnly?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Trading',
    authOnly: true,
    items: [
      { to: '/positions', label: 'Positions' },
      { to: '/pnl-monitor', label: 'PnL' },
      { to: '/trades', label: 'Trades' },
      { to: '/trade-calendar', label: 'Trade Calendar' },
      { to: '/quick-trade', label: 'Quick Trade' },
      { to: '/screener', label: 'AI Screener' },
    ],
  },
  {
    label: 'Market',
    authOnly: true,
    items: [
      { to: '/market', label: 'Charts' },
      { to: '/scanner', label: 'Scanner' },
      { to: '/daily-edge', label: 'Daily Edge' },
      { to: '/watchlist', label: 'Watchlist' },
    ],
  },
  {
    label: 'AI',
    authOnly: true,
    items: [
      { to: '/strategist', label: 'Strategist' },
      { to: '/scalper', label: 'Scalper' },
      { to: '/leaderboard', label: 'Leaderboard' },
      { to: '/predictions', label: 'Predictions' },
    ],
  },
  {
    label: 'Reports',
    authOnly: true,
    items: [
      { to: '/reports', label: 'Trade Reports' },
      { to: '/earnings', label: 'Earnings' },
      { to: '/errors', label: 'Error Log' },
    ],
  },
  {
    label: 'Playbook',
    items: [{ to: '/beanstalk', label: 'Beanstalk' }],
  },
  {
    label: 'More',
    items: [
      { to: '/learn', label: 'Learn' },
      { to: '/glossary', label: 'Glossary' },
    ],
  },
];

const NavDropdown: React.FC<{
  group: NavGroup;
  openDropdown: string | null;
  setOpenDropdown: (label: string | null) => void;
  isMobile: boolean;
  mobileExpanded: string[];
  toggleMobileExpanded: (label: string) => void;
}> = ({ group, openDropdown, setOpenDropdown, isMobile, mobileExpanded, toggleMobileExpanded }) => {
  const location = useLocation();
  const isChildActive = group.items.some((item) => location.pathname === item.to);
  const isOpen = openDropdown === group.label;
  const isMobileOpen = mobileExpanded.includes(group.label);

  if (isMobile) {
    return (
      <li className={styles.dropdownItem}>
        <button
          className={`${styles.dropdownTrigger} ${isChildActive ? styles.dropdownTriggerActive : ''}`}
          onClick={() => toggleMobileExpanded(group.label)}
        >
          {group.label}
          <span className={`${styles.chevron} ${isMobileOpen ? styles.chevronOpen : ''}`} />
        </button>
        {isMobileOpen && (
          <ul className={styles.dropdownSubmenu}>
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to}>{item.label}</NavLink>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li
      className={styles.dropdownItem}
      onMouseEnter={() => setOpenDropdown(group.label)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button
        className={`${styles.dropdownTrigger} ${isChildActive ? styles.dropdownTriggerActive : ''}`}
        onClick={() => setOpenDropdown(isOpen ? null : group.label)}
      >
        {group.label}
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
      </button>
      {isOpen && (
        <ul className={styles.dropdownMenu}>
          {group.items.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} onClick={() => setOpenDropdown(null)}>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Navbar: React.FC = () => {
  const { user, signOut, isAdmin } = useAuth();
  const { isConnected } = useAlpaca();
  const { setMode, isLive } = useAccountMode();
  const { toggleTheme, isDark, isAuto, setAuto } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string[]>([]);
  const navRef = useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen && !openDropdown) return;
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen, openDropdown]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const toggleMobileExpanded = (label: string) => {
    setMobileExpanded((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
  };

  const visibleGroups = NAV_GROUPS.filter((g) => !g.authOnly || user);

  return (
    <nav className={styles.navbar} ref={navRef}>
      {/* Mobile: logo + hamburger */}
      <div className={styles.mobileHeader}>
        <NavLink to="/home" className={styles.mobileLogo}>
          <img src={logoIcon} alt="SignalDeck" className={styles.logoImg} />
        </NavLink>
        <button
          className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <ul className={`${styles.navLinks} ${menuOpen ? styles.navLinksOpen : ''}`}>
        <li>
          <NavLink to="/home">Home</NavLink>
        </li>

        {visibleGroups.map((group) => (
          <NavDropdown
            key={group.label}
            group={group}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            isMobile={isMobile}
            mobileExpanded={mobileExpanded}
            toggleMobileExpanded={toggleMobileExpanded}
          />
        ))}

        {user && (
          <li className={styles.toggleGroup}>
            <div className={styles.pillToggle}>
              <button
                className={`${styles.pillOption} ${!isLive ? styles.pillActive : ''}`}
                onClick={() => setMode('paper')}
              >
                Paper
              </button>
              <button
                className={`${styles.pillOption} ${isLive ? styles.pillActive : ''} ${isLive ? styles.pillLive : ''} ${!isAdmin ? styles.pillDisabled : ''}`}
                onClick={() => isAdmin && setMode('live')}
                title={!isAdmin ? 'Admin access required for live account' : ''}
              >
                Live {!isAdmin && '🔒'}
              </button>
            </div>
            <div className={styles.themeGroup}>
              <button
                className={styles.themeToggle}
                onClick={toggleTheme}
                title={isAuto ? 'Auto mode on — click to override' : 'Toggle dark mode'}
              >
                {isDark ? '\u2600\uFE0F' : '\uD83C\uDF19'}
              </button>
              <button
                className={`${styles.autoBtn} ${isAuto ? styles.autoBtnActive : ''}`}
                onClick={() => setAuto(!isAuto)}
                title={
                  isAuto ? 'Auto scheduling on (8PM-8AM dark) — click to disable' : 'Enable auto dark mode (8PM-8AM)'
                }
              >
                AUTO
              </button>
            </div>
            <div className={styles.connectionStatus}>
              {isConnected ? (
                <>
                  <span className={styles.connectionDot} />
                  <span className={styles.connectionText}>Connected</span>
                </>
              ) : (
                <NavLink to="/onboarding" className={styles.connectionLink}>
                  <span className={styles.connectionDotDisconnected} />
                  <span className={styles.connectionText}>Not Connected</span>
                </NavLink>
              )}
            </div>
          </li>
        )}
        {user && (
          <li className={styles.notifItem}>
            <NotificationBell />
          </li>
        )}
        {user && (
          <li>
            <NavLink to="/settings" className={({ isActive }) => (isActive ? styles.active : '')}>
              Settings
            </NavLink>
          </li>
        )}
        <li className={styles.authItem}>
          {user ? (
            <button className={styles.authButton} onClick={handleSignOut}>
              Logout
            </button>
          ) : (
            <NavLink to="/login">Login</NavLink>
          )}
        </li>
      </ul>
    </nav>
  );
};

export default Navbar;
